import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { C, FONT, MONO } from './landingTokens';
import { DEPOT, HERO_CARDS, HERO_TRIPS, HeroTrip, ROADS } from './heroMapData';

/**
 * The hero's right-hand panel: one card holding a drawn map of Pune in the
 * style of LogiOpt's hero (pale roads, thin coloured routes, teardrop pins,
 * small top-down trucks), with five simulated vans out of one depot and a
 * one-line trip summary underneath.
 *
 * The map is a single SVG drawn from data in heroMapData.ts (OpenStreetMap
 * roads and OSRM routes, all in one pixel frame). Nothing is fetched at runtime,
 * and the SVG scales with the card in plain CSS, so it needs no measuring and
 * works the same inside the zoomed page on large screens.
 */

// The part of the frame the card shows, centred on the routes (x 381..1200,
// y 97..721) with room above for the top pin's label. Drawn with "xMidYMid
// slice": a narrower box crops the sides evenly. The routes and labels need
// about 940 units of width, so the box must be at least ~1.36x as wide as tall;
// the viewBox itself is 1.59x. MAP_HEIGHT keeps the box in that range.
const VIEW = { x: 240, y: 55, w: 1100, h: 690 };

// Map height: on desktop, whatever the first screen has left after the header,
// the hero's padding and the card's header and footer (256px in all), within
// 63-73% of the card's width (container units). On phones, a fixed shape.
const MAP_HEIGHT = {
  xs: '68cqw',
  md: 'min(73cqw, 520px, max(63cqw, calc(100vh / var(--lp-zoom, 1) - 256px)))',
};

const CARD_SHADOW = '0 24px 48px -28px rgba(18,23,42,0.35)';

// Map palette, after LogiOpt's: near-white ground, lavender roads, pale river.
const MAP = {
  ground: '#f7f8fc',
  river: '#dbe6fa',
  major: '#cdd3ee',
  minor: '#e2e5f3',
  label: '#3d4255',
};

// Route paths are under 1200 frame pixels long, so one dash of 1200 hides a path
// fully at the start of the draw-in.
const DASH = 1200;

const RTO_RED = '#dc2626';

// Labels for pins east of this x go on the pin's left.
const LABEL_FLIP_X = 1120;

// Teardrop pin with its tip at 0,0 and its head centred at 0,-15 (radius 9).
const PIN = 'M0 0 C-2 -6 -9 -9 -9 -15 A9 9 0 1 1 9 -15 C9 -9 2 -6 0 0 Z';

const routeId = (trip: HeroTrip) => `hero-route-${trip.id}`;

const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  px: 1.25,
  py: '3px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
};

/** Top-down delivery truck facing +x, about 34 x 16 frame pixels. */
const Truck: React.FC<{ color: string }> = ({ color }) => (
  <g transform="scale(1.3)">
    <rect x={-13} y={-6} width={17} height={12} rx={2} fill="#ffffff" stroke={color} strokeWidth={1.6} />
    <rect x={-10.5} y={-3.8} width={5.2} height={4} rx={0.6} fill="#d9a85c" />
    <rect x={-4.8} y={-0.4} width={5.2} height={4} rx={0.6} fill="#c48a3f" />
    <rect x={4.5} y={-5.5} width={8.5} height={11} rx={2.5} fill={color} />
    <rect x={9} y={-4} width={2.2} height={8} rx={1} fill="#ffffff" opacity={0.75} />
    <circle cx={13.2} cy={-3.6} r={1.1} fill="#ffd166" />
    <circle cx={13.2} cy={3.6} r={1.1} fill="#ffd166" />
  </g>
);

/** A place label with a white halo so it reads over roads and routes. */
const PlaceLabel: React.FC<{ x: number; y: number; children: React.ReactNode; bold?: boolean; anchor?: 'start' | 'end' }> = ({
  x,
  y,
  children,
  bold,
  anchor = 'start',
}) => (
  <text
    x={x}
    y={y}
    textAnchor={anchor}
    fontFamily={MONO}
    fontSize={15}
    fontWeight={bold ? 700 : 500}
    fill={MAP.label}
    stroke="#ffffff"
    strokeWidth={4}
    strokeLinejoin="round"
    paintOrder="stroke"
  >
    {children}
  </text>
);

const creditLinkSx = { color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };

/** OpenStreetMap credit (ODbL), behind an ⓘ button so it stays out of the way. */
const MapCredit: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        position: 'absolute',
        right: 8,
        bottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        '& .credit-text': { display: open ? 'block' : 'none' },
        '&:hover .credit-text, &:focus-within .credit-text': { display: 'block' },
      }}
    >
      <Box
        className="credit-text"
        sx={{ px: '6px', borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.92)', fontFamily: FONT, fontSize: 10.5, lineHeight: '18px', color: C.body }}
      >
        Map data{' '}
        <Box component="a" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" sx={creditLinkSx}>
          © OpenStreetMap contributors
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="Map credits"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        sx={{
          width: 20,
          height: 20,
          p: 0,
          border: 0,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.92)',
          color: C.body,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          fontWeight: 700,
          fontSize: 12,
          lineHeight: '20px',
          cursor: 'pointer',
          boxShadow: '0 0 0 1px rgba(18,23,42,0.12)',
          '&:focus-visible': { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
        }}
      >
        i
      </Box>
    </Box>
  );
};

const MAP_LABEL =
  'Simulated delivery day on a map of Pune: five vans leave one depot on road routes across the city, with delivered and pending drops and one return to origin flagged.';

const HeroMap: React.FC<{ reduceMotion: boolean }> = ({ reduceMotion }) => (
  <Box
    sx={{
      bgcolor: '#ffffff',
      border: `1px solid ${C.line}`,
      borderRadius: '18px',
      boxShadow: CARD_SHADOW,
      overflow: 'hidden',
      containerType: 'inline-size',
      fontFamily: FONT,
      color: C.ink,
      '@keyframes heroDraw': { from: { strokeDashoffset: DASH }, to: { strokeDashoffset: 0 } },
      '@keyframes heroFade': { from: { opacity: 0 }, to: { opacity: 1 } },
    }}
  >
    {/* Header: what the card is, and that it is simulated. */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.75 }}>
      <Box component="span" sx={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {HERO_CARDS.title}
      </Box>
      <Box component="span" sx={{ ...pillSx, bgcolor: '#e6f4ec', color: '#17794a' }}>
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#17794a' }} />
        {HERO_CARDS.status}
      </Box>
      <Box
        component="span"
        sx={{
          ...pillSx,
          ml: 'auto',
          border: `1px solid ${C.line}`,
          color: C.muted,
          fontFamily: MONO,
          fontSize: 10.5,
          letterSpacing: '0.08em',
        }}
      >
        SIMULATED
      </Box>
    </Box>

    {/* The map. */}
    <Box
      sx={{
        position: 'relative',
        height: MAP_HEIGHT,
        bgcolor: MAP.ground,
        borderTop: `1px solid ${C.lineSoft}`,
        borderBottom: `1px solid ${C.lineSoft}`,
      }}
    >
      <svg
        viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-label={MAP_LABEL}
        style={{ display: 'block' }}
      >
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d={ROADS.river} stroke={MAP.river} strokeWidth={7} />
          <path d={ROADS.minor} stroke={MAP.minor} strokeWidth={1.6} />
          <path d={ROADS.major} stroke={MAP.major} strokeWidth={2.6} />
        </g>

        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          {HERO_TRIPS.map((trip, index) => {
            const draw = `heroDraw 2.2s cubic-bezier(.3,.6,.3,1) ${0.3 + index * 0.25}s both`;
            return (
              <g key={trip.id}>
                <path d={trip.d} stroke="#ffffff" strokeWidth={6.5} strokeDasharray={DASH} style={{ animation: draw }} />
                <path
                  id={routeId(trip)}
                  d={trip.d}
                  stroke={trip.color}
                  strokeWidth={3.2}
                  strokeDasharray={DASH}
                  style={{ animation: draw }}
                />
              </g>
            );
          })}
        </g>

        {/* Drops as pins: filled when delivered, outlined when pending, red for
            the RTO. Each van's last drop is labelled. */}
        <g style={{ animation: 'heroFade .6s ease-out 2.6s both' }}>
          {HERO_TRIPS.flatMap(trip =>
            trip.stops.map(([x, y], index) => {
              const stop = index + 1;
              const rto = stop === trip.rtoStop;
              const delivered = stop <= trip.delivered;
              const fill = rto ? RTO_RED : delivered ? trip.color : '#ffffff';
              const ring = rto ? RTO_RED : trip.color;
              return (
                <g key={`${trip.id}-${stop}`} transform={`translate(${x} ${y})`}>
                  <path d={PIN} fill={fill} stroke={rto || delivered ? '#ffffff' : ring} strokeWidth={2} />
                  <circle cx={0} cy={-15} r={3.6} fill={rto || delivered ? '#ffffff' : ring} />
                </g>
              );
            }),
          )}
          {HERO_TRIPS.map(trip => {
            const [x, y] = trip.stops[trip.stops.length - 1];
            // Pins near the right edge get their label on the left, so narrow
            // cards (which crop the sides) do not cut it off.
            const left = x > LABEL_FLIP_X;
            return (
              <PlaceLabel key={trip.id} x={left ? x - 12 : x + 12} y={y - 11} anchor={left ? 'end' : 'start'}>
                {trip.names[trip.names.length - 1]}
              </PlaceLabel>
            );
          })}
        </g>

        <rect x={DEPOT[0] - 8} y={DEPOT[1] - 8} width={16} height={16} rx={2} fill={C.navy} stroke="#ffffff" strokeWidth={2.5} />
        <PlaceLabel x={DEPOT[0] + 14} y={DEPOT[1] - 8} bold>
          {HERO_CARDS.depotLabel}
        </PlaceLabel>

        {/* Trucks drive from the depot to where each trip has got to, follow the
            road's direction, then fade and start again. Hidden with reduced motion. */}
        {!reduceMotion &&
          HERO_TRIPS.map((trip, index) => {
            const start = `${2.5 + index * 0.4}s`;
            return (
              <g key={trip.id} opacity={0}>
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.04;0.92;1"
                  dur={`${trip.loop}s`}
                  begin={start}
                  repeatCount="indefinite"
                />
                <animateMotion
                  dur={`${trip.loop}s`}
                  begin={start}
                  repeatCount="indefinite"
                  rotate="auto"
                  calcMode="spline"
                  keyTimes="0;0.82;1"
                  keyPoints={`0;${trip.reach};${trip.reach}`}
                  keySplines="0.4 0 0.6 1;0 0 1 1"
                >
                  <mpath href={`#${routeId(trip)}`} />
                </animateMotion>
                <Truck color={trip.color} />
              </g>
            );
          })}
      </svg>

      <Box
        sx={{
          position: 'absolute',
          left: 10,
          bottom: 8,
          px: 1.25,
          py: '4px',
          borderRadius: 999,
          bgcolor: 'rgba(255,255,255,0.92)',
          border: `1px solid ${C.line}`,
          fontFamily: MONO,
          fontSize: 11,
          color: C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {HERO_CARDS.caption}
      </Box>
      <MapCredit />
    </Box>

    {/* Footer: one line of trip progress. */}
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 2,
        rowGap: 0.75,
        px: 2.5,
        py: 1.5,
        fontSize: 12.5,
      }}
    >
      {HERO_TRIPS.map(trip => (
        <Box key={trip.id} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: trip.color }} />
          <Box component="span" sx={{ fontWeight: 600 }}>
            {trip.id}
          </Box>
          <Box component="span" sx={{ fontFamily: MONO, color: C.body }}>
            {trip.delivered}/{trip.stops.length}
          </Box>
        </Box>
      ))}
      <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.75, color: '#b91c1c', fontWeight: 600 }}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RTO_RED }} />
        {HERO_CARDS.exception}
      </Box>
    </Box>
  </Box>
);

export default HeroMap;
